/* eslint-disable @typescript-eslint/no-empty-function */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  ReactElement,
  Dispatch,
  SetStateAction
} from 'react'
import { message } from 'antd'
import clsx from 'clsx'
import { Icon } from '@iconify/react'

import styles from './message.module.css'

type MessageType = 'error' | 'warning' | 'success' | 'info' | undefined
export type MessageProps = {
  type: MessageType
  message: string | undefined | ReactNode
  className?: string
  key?: string
}

// default value is added in the component
const MessageContext = createContext<Dispatch<SetStateAction<MessageProps>>>(
  () => {}
)

const initialState: MessageProps = {
  message: '',
  type: undefined,
  className: ''
}

const MessageProvider = ({
  children
}: {
  children: ReactNode
}): ReactElement => {
  const [state, setState] = useState(initialState)
  useEffect(() => {
    // reset last message in 2 seconds interval
    if (state.message) {
      const timer = setInterval(() => {
        setState(initialState)
      }, 2000)
      return () => clearInterval(timer)
    }
  }, [state.message])

  useEffect(() => {
    if (state.message) {
      let messageFunction = message.error
      let messageClassName = ''
      let icon
      switch (state.type) {
        case 'success':
          messageFunction = message.success
          messageClassName = styles.success
          icon = (
            <Icon
              icon="lucide:check-circle"
              width="14"
              color="var(--color-green-700)"
            />
          )
          break
        case 'warning':
          messageFunction = message.warning
          messageClassName = styles.warning
          icon = (
            <Icon
              icon="lucide:alert-triangle"
              width="14"
              color="var(--color-yellow-700)"
            />
          )

          break
        case 'info':
          messageFunction = message.info
          messageClassName = styles.info
          icon = (
            <Icon icon="lucide:info" width="14" color="var(--color-blue-700)" />
          )

          break
        case 'error':
          console.error(state.message)
          messageFunction = message.error
          messageClassName = styles.error
          icon = (
            <Icon
              icon="lucide:x-circle"
              width="14"
              color="var(--color-orange-700)"
            />
          )

          break
        default:
          messageFunction = message.error
          break
      }

      // ignore invalid jwt token message
      if (
        typeof state.message === 'string' &&
        state.message.toLowerCase() === 'invalid jwt token'
      )
        return

      messageFunction({
        content: state.message,
        className: clsx(state.className, styles.message, messageClassName),
        icon,
        ...(state.key ? { key: state.key } : {})
      })
    }
  }, [state])

  return (
    <MessageContext.Provider value={setState}>
      {children}
    </MessageContext.Provider>
  )
}

// hooks for acessing context
const useShowMessage = (): Dispatch<SetStateAction<MessageProps>> => {
  const showMessage = useContext(MessageContext)
  return showMessage
}

export { MessageProvider, useShowMessage }
